import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    TextField,
    Tooltip,
    Typography,
    DialogContent,
    useTheme,
    capitalize,
} from "@mui/material";
import React, { useState } from "react";
import * as Yup from "yup";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import queryGraphql from "../../../helpers/ApiInterface";
import { useAuth } from "../../../helpers/AuthProvider";
import { Close } from "@mui/icons-material";
import { Formik, ErrorMessage, Form } from "formik";

import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import MiniBot from "../../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";

// --- Interfaces ---
interface ApplicationInfo {
    applicationName: string;
}

interface SolutionActorInfo {
    name: string;
    email?: string;
    googleProfilePicture: string;
}

interface RAGNestedDetails {
    projectId: string;
    databaseType: string;
    embeddingModelId: string;
    chunkingStrategy: string;
    chunkSize: string;
    chunkOverlap: string;
    region: string;
    network?: string;
    subnets?: string;
    serviceAccount?: string;
    databaseConnectionSecret?: string;
}

interface RAGDetailsData {
    id: string;
    name: string;
    solutionCategoryType: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    environment: string;
    status: string;
    details: {
        rag: RAGNestedDetails;
    };
    solutionOwner: SolutionActorInfo;
    solutionApprover?: SolutionActorInfo;
    application?: ApplicationInfo;
    reasonForRejection?: string;
}

interface ApprovalFormValues {
    network: string;
    subnets: string;
    serviceAccount: string;
    databaseConnectionSecret: string;
}

// --- Validation Schemas ---
const approvalValidationSchema = () =>Yup.object().shape({
    network: Yup.string().required("This field is required"),
    subnets: Yup.string().required("This field is required"),
    serviceAccount: Yup.string().required("This field is required"),
    databaseConnectionSecret: Yup.string().required("This field is required"),
});

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
});

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

// --- Main Content ---
const RAGApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [rag, setRag] = useState<RAGDetailsData | null>(null);
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const fallback = '-';
    const theme = useTheme();

    React.useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                const data = await queryGraphql(
                    `
                    query GetRAG($getRagId: ID!) {
                      getRAG(id: $getRagId) {
                        id, name, solutionCategoryType, cloudProvider, environment, status, reasonForRejection, createdAt, updatedAt,
                        details { rag { projectId, databaseType, embeddingModelId, chunkingStrategy, chunkSize, chunkOverlap, region, network, subnets, serviceAccount, databaseConnectionSecret } }
                        solutionOwner { id, name, email, googleProfilePicture }
                        solutionApprover { id, name, email, googleProfilePicture }
                        application { id, applicationName }
                      }
                    }
                    `, { getRagId: id }
                );
                if (data.getRAG) {
                    setRag(data.getRAG);
                } else {
                    console.log("RAG details not found.");
                    setRag(null);
                }
            } catch (error) {
                console.log("Error fetching RAG details:", error);
            }
        };
        fetchData();
    }, [id]);

    const statusDisplay = rag?.status ? rag.status : "Status Unknown";
    const statusBgColor = rag?.status === "active" || rag?.status === "approved" ? "#12B76A10" : rag?.status === "rejected" ? "#F0443810" : "#FDB02210";
    const statusIcon = rag?.status === "active" || rag?.status === "approved" ? "/assets/tickcircle.svg" : rag?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg";
    const projectIdLabel = rag?.cloudProvider === 'aws' ? 'Account ID' : rag?.cloudProvider === 'azure' ? 'Subscription ID' : 'Project ID';

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/aisolutions/requests/rag" onClick={(e) => { e.preventDefault(); navigate("/aisolutions/requests/rag") }}>
                        RAG Requests
                    </Link>
                    <Typography color="text.primary">RAG Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/requests/rag")} >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">RAG Details</Typography>
                </Box>
            </Box>
            <Grid container justifyContent="center">
                <Grid item xs={11} md={11} lg={11}> 
                    {rag && (
                        <Formik
                            initialValues={{
                                network: rag.details.rag.network || "",
                                subnets: rag.details.rag.subnets || "",
                                serviceAccount: rag.details.rag.serviceAccount || "",
                                databaseConnectionSecret: rag.details.rag.databaseConnectionSecret || "",
                            }}
                            validationSchema={approvalValidationSchema()}
                            enableReinitialize
                            onSubmit={async (values) => {
                                if (!id) return;
                                const detailsPayload = { rag: { ...values } };
                                try {
                                    const approvalData = await queryGraphql(
                                        `mutation ApproveSolutionRAG($input: ApproveSolutionInput!) { approveSolutionRAG(input: $input) { id } }`,
                                        { input: { id: id, details: detailsPayload } }
                                    );
                                    if (approvalData.approveSolutionRAG?.id) {
                                        setOpenApproveModal(false);
                                        navigate(0);
                                    } else { console.error("Approval failed", approvalData); }
                                } catch (error) { console.error("Error during RAG approval:", error); }
                            }}
                        >
                            {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                                <Form id="approve-rag-main-form">
                                    <Box my={2}>
                                        <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                                                <Box sx={{mb: {xs: 1, sm: 0}}}>
                                                    <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{rag.name || fallback}</Typography>
                                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${rag.solutionOwner?.name || fallback}`}</Typography>
                                                </Box>
                                                <Box textAlign="right">
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'flex-end' }}>
                                                        <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
                                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{textTransform: 'capitalize'}}>{statusDisplay}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                        
                                        <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Request Details</Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <Grid container spacing={2.5}>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label={projectIdLabel} value={rag.details.rag.projectId} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Cloud Provider" value={rag.cloudProvider?.toUpperCase()} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Region" value={rag.details.rag.region} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Application Name" value={rag.application?.applicationName} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Category Type" value={rag.solutionCategoryType} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Database Type" value={rag.details.rag.databaseType} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Embedding Model ID" value={rag.details.rag.embeddingModelId} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Chunking Strategy" value={rag.details.rag.chunkingStrategy} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Chunk Size" value={rag.details.rag.chunkSize} /></Grid>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Chunk Overlap" value={rag.details.rag.chunkOverlap} /></Grid>
                                               
                                                    {/* <Grid item xs={12} sm={6} md={3}>
                                                        <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">Actioned by</Typography>
                                                        <Box display="flex" alignItems="center" mt={0.5}>
                                                            <Avatar sx={{ height: "24px", width: "24px", mr: 1 }} src={rag.solutionApprover?.googleProfilePicture} alt={rag.solutionApprover?.name?.charAt(0)}/>
                                                            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" noWrap> {rag.solutionApprover?.name || fallback} </Typography>
                                                        </Box>
                                                    </Grid>
                                                    {rag.status === 'rejected' && 
                                                        <Grid item xs={12} sm={6} md={9}><DetailItem label="Reason for Rejection" value={rag.reasonForRejection} /></Grid>
                                                    } */}
                                                

                                            </Grid>
                                        </Box>

                                         {rag.status === 'rejected' && (
                                            <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                            <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                                            <Divider sx={{ marginY: 2 }} />
                                            <DetailItem label="Reason for Rejection" value={rag.reasonForRejection} />
                                            </Box>
                                    )}

                                        
                                        
                                       

                                        {rag.status === 'pending' && (
                                            <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <Grid container spacing={2.5}>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Network" /><TextField name="network" value={values.network} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Network" variant="filled" fullWidth hiddenLabel error={touched.network && Boolean(errors.network)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="network" /></div></Grid>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Subnets" /><TextField name="subnets" value={values.subnets} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Subnets" variant="filled" fullWidth hiddenLabel error={touched.subnets && Boolean(errors.subnets)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="subnets" /></div></Grid>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Database Connection Secret" /><TextField name="databaseConnectionSecret" value={values.databaseConnectionSecret} onChange={handleChange} onBlur={handleBlur} placeholder="Enter DB Connection Secret" variant="filled" fullWidth hiddenLabel error={touched.databaseConnectionSecret && Boolean(errors.databaseConnectionSecret)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="databaseConnectionSecret" /></div></Grid>
                                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Service Account" /><TextField name="serviceAccount" value={values.serviceAccount} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Service Account" variant="filled" fullWidth hiddenLabel error={touched.serviceAccount && Boolean(errors.serviceAccount)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="serviceAccount" /></div></Grid>
                                                </Grid>
                                            </Box>
                                        )}

                                         {rag.status === 'active' && (
                                             <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Cloud Infra Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <Grid container spacing={2.5}>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Network" value={rag.details.rag.network} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Subnets" value={rag.details.rag.subnets} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Database Connection Secret" value={rag.details.rag.databaseConnectionSecret} /></Grid>
                                                    <Grid item xs={12} sm={6} md={3}><DetailItem label="Service Account" value={rag.details.rag.serviceAccount} /></Grid>
                                                </Grid>
                                             </Box>
                                        )}
                                    </Box>

                                    
                                   

                                    {rag?.status === "pending" && user.roles.some((role: string) => ["infrastructure_admin"].includes(role)) && (
                                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={3}>
                                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold" }} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
                                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "rgba(0,0,0,0.85)"}, textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&.Mui-disabled': { backgroundColor: theme.palette.action.disabledBackground || 'rgba(0, 0, 0, 0.12)', color: theme.palette.action.disabled || 'rgba(0, 0, 0, 0.26)' } }} variant="contained" size="large" disabled={!isValid || !dirty} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                                        </Box>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
            </Grid>

            <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                        <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Request</Typography>
                        <IconButton aria-label="close" onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton>
                    </Box>
                    <Divider />
                    <DialogContent>
                        <Typography>Are you sure you want to approve this RAG request?</Typography>
                         <Box sx={{p:2, backgroundColor:'#FAFAFA', borderRadius:'8px', mt: 2}}>
                           {rag && <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={6}><DetailItem label="RAG Name" value={rag.name} /></Grid>
                                <Grid item xs={12} sm={6}><DetailItem label={projectIdLabel} value={rag.details.rag.projectId} /></Grid>
                            </Grid>}
                        </Box>
                    </DialogContent>
                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                        <Button type="submit" form="approve-rag-main-form" sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
                    </Box>
                </Box>
            </Modal>

            <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px", display: 'flex', flexDirection: 'column' }}>
                    <Formik
                        initialValues={{ reasonForRejection: "" }}
                        validationSchema={rejectValidationSchema}
                        onSubmit={async ({ reasonForRejection }) => {
                            if (!id) return;
                            try {
                                const rejectionData = await queryGraphql(
                                    `mutation RejectSolutionRAG($input: RejectSolutionInput!) { rejectSolutionRAG(input: $input) { id name } }`,
                                    { input: { id: id, reasonForRejection } }
                                );
                                if (rejectionData.rejectSolutionRAG?.id) {
                                    setOpenRejectModal(false);
                                    navigate(0);
                                }
                            } catch (error) { console.error("Error during rejection:", error); }
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                            <Form id="reject-rag-form" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                                    <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject RAG Request</Typography>
                                    <IconButton aria-label="close" onClick={() => setOpenRejectModal(false)}><Close fontSize="small" /></IconButton>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 3, flexGrow: 1 }}>
                                    <CompulsoryFieldsLabel fieldName="Comments" />
                                    <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                    <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                    <Button variant="outlined" onClick={() => setOpenRejectModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                    <Button type="submit" form="reject-rag-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained">Reject</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </Container>
    );
};

const RAGApproveFormPage: React.FC = () => {
    const theme = useTheme();
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
    };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-rag"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <RAGApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default RAGApproveFormPage;